<template>
  <loading-page v-if="fetchingApiKeys" />
  <div
    v-else
    class="apikeys table-wrapper"
  >
    <div
      :class="{
        'default-table':
          currentBreakPoint === 'lg' || currentBreakPoint === 'xl',
      }"
    >
      <div class="px-1 pb-1 pt-2 d-flex">
        <div class="text-md text-main text-thick">
          Captain API Keys
        </div>
        <span
          v-if="$can('create', 'apikeys', 'captain')"
          class="text-primary ml-1 cursor-pointer"
          @click="$router.push({ name: 'new-api-key' })"
        >
          + Generate New
        </span>
      </div>
      <div class="position-relative h-100 d-none d-lg-block">
        <b-table
          ref="table"
          :items="apiKeys"
          :fields="fields"
          :class="{ loaded: !fetchingApiKeys }"
          show-empty
          responsive
          class="more-visible"
        >
          <template #empty>
            <div
              v-if="fetchingApiKeys"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <no-data
              v-else
              no-data-text="You haven't created any API keys yet..."
              create-text="+ Create API key"
              access-page="API Keys"
              icon="Security"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'new-api-key' })"
            />
          </template>
          <template #cell(name)="data">
            <router-link
              :to="{ name: 'view-api-key', params: { id: data.item.id } }"
            >
              {{ data.value }}
            </router-link>
          </template>

          <template #cell(id)="data">
            <a
              v-if="$can('update', 'apikeys', 'captain')"
              @click="
                $router.push({
                  name: 'edit-api-key',
                  params: { id: data.value },
                })
              "
            >
              <GjIcon
                class="icon-right"
                name="Edit"
              />
            </a>
            <template v-if="$can('delete', 'apikeys', 'captain')">
              <b-spinner
                v-if="removingApiKeyId == data.value && deletingApiKey"
              />
              <a
                v-else
                @click="deleteApiKey(data.value)"
              >
                <GjIcon
                  class="icon-right"
                  name="Delete"
                />
              </a>
            </template>
          </template>
        </b-table>
      </div>
      <div class="position-relative d-lg-none">
        <card
          v-for="apiKey in apiKeys"
          :key="apiKey.id"
          :fields="fields"
          :item="apiKey"
        >
          <template #options>
            <b-dropdown
              id="dropdown-1"
              right
              variant="white"
            >
              <template #button-content>
                <GjIcon name="Moreoptions" />
              </template>
              <b-dropdown-item
                v-if="$can('update', 'apikeys', 'captain')"
                @click="
                  $router.push({
                    name: 'edit-api-key',
                    params: { id: apiKey.id },
                  })
                "
              >
                Edit
              </b-dropdown-item>
              <b-dropdown-item
                v-if="$can('delete', 'apikeys', 'captain')"
                @click="deleteApiKey(apiKey.id)"
              >Delete</b-dropdown-item>
            </b-dropdown>
          </template>
          <template #name>
            <router-link
              :to="{ name: 'view-api-key', params: { id: apiKey.id } }"
            >
              {{ apiKey.name }}
            </router-link>
          </template>
        </card>
        <div
          v-if="fetchingApiKeys"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!fetchingApiKeys && apiKeys.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="You haven't created any API keys yet..."
              create-text="+ Create API key"
              access-page="API Keys"
              icon="Security"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'new-api-key' })"
            />
          </paper-simple>
        </template>
      </div>
    </div>

  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'

export default {
  name: 'ApiKeysView',
  components: { Card },
  data() {
    return {
      fields: [
        { key: 'name', isPrimary: true },
        { key: 'role', isPrimary: true },
        'scope',
        { key: 'id', label: 'Actions', hide: true },
      ],
      removingApiKeyId: null,
      hasPermissions: true,
    }
  },
  computed: {
    apiKeys() {
      return this.$store.state.apiKeys.apiKeys
    },
    fetchingApiKeys() {
      return this.$store.state.apiKeys.fetchingApiKeys
    },
    deletingApiKey() {
      return this.$store.state.apiKeys.deletingApiKey
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
  },
  watch: {
    currentPage() {
      this.fetchApiKeys()
    },
  },
  created() {
    this.fetchApiKeys()
    if (this.$route.params.toast) {
      const { toast } = this.$route.params
      this.generalToast(toast.message, toast.title, toast.variant)
    }
  },
  methods: {
    fetchApiKeys() {
      this.$store.dispatch('apiKeys/fetchApiKeys').catch(error => {
        if (error.response?.status === 403) {
          this.hasPermissions = false
          return
        }
        if (error.response.status !== 404) {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching api keys.',
          )
        }
      })
    },
    deleteApiKey(id) {
      this.confirmDelete(
        'Delete',
        'Are you sure you want to delete this key?',
      ).then(ok => {
        if (ok) {
          this.removingApiKeyId = id
          this.$store
            .dispatch('apiKeys/deleteApiKey', id)
            .then(() => {
              this.successToast('API Key deleted successfully')
              // this.fetchApiKeys()
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.apikeys {
  .default-table {
    margin-top: 0;
    box-shadow: none;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 2px 8px rgb(5 45 97 / 8%);
    border-radius: 4px;
    .table-responsive {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-direction: column;
      ::v-deep {
        table {
          border-bottom: 0;
          margin-top: 0;
          thead {
            th:last-child {
              width: 30px;
            }
          }
          .spinner-border {
            margin-left: 5px;
            width: 15px;
            height: 15px;
          }
        }
      }
    }
  }
}
</style>
